<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <div class="sa-head">
          <router-link :to="listLink" class="sa-back" aria-label="Back to list"><i class="fas fa-arrow-left"></i></router-link>
          <div class="sa-head-text">
            <h3>
              {{ title }}
              <span v-if="header.adjustmentno" class="sa-head-no">{{ header.adjustmentno }}</span>
              <span v-if="adjustmentId" class="sa-status" :class="`st-${header.status.toLowerCase()}`">{{ statusLabel }}</span>
            </h3>
            <p>{{ subtitle }}</p>
          </div>
        </div>

        <div v-if="readonly" class="sa-banner" :class="`banner-${header.status.toLowerCase()}`">
          <i class="fas" :class="header.status === 'POSTED' ? 'fa-check-circle' : 'fa-ban'"></i>
          <span v-if="header.status === 'POSTED'">Posted {{ formatDateTime(header.postedat) }} &mdash; stock has been updated. Use <strong>Cancel &amp; reverse</strong> to undo it.</span>
          <span v-else>Cancelled {{ formatDateTime(header.cancelledat) }} &mdash; any stock changes were reversed.</span>
        </div>

        <div v-if="loading" class="sa-card sa-loading"><div class="sa-spinner"></div>Loading...</div>

        <div v-else class="sa-layout">
          <div class="sa-main">
            <!-- ═════════════ ADJUSTMENT ═════════════ -->
            <template v-if="!isCount">
              <div v-if="!readonly" class="sa-card sa-picker-card">
                <div class="picker">
                  <i class="fas fa-search picker-icon" aria-hidden="true"></i>
                  <input
                    ref="pickerInput"
                    v-model.trim="pickerSearch"
                    type="search"
                    class="picker-input"
                    placeholder="Search or scan an item to add..."
                    autocomplete="off"
                    @input="searchItems"
                    @keydown.down.prevent="movePicker(1)"
                    @keydown.up.prevent="movePicker(-1)"
                    @keydown.enter.prevent="pickActive"
                    @keydown.esc="pickerSearch = ''; pickerResults = []"
                  />
                  <span class="picker-kbd"><i class="fas fa-barcode"></i> scan works too</span>
                  <div v-if="pickerSearch && (pickerResults.length || pickerLoading || pickerSearched)" class="picker-results">
                    <div v-if="pickerLoading" class="picker-empty">Searching...</div>
                    <template v-else-if="pickerResults.length">
                      <button
                        v-for="(result, index) in pickerResults"
                        :key="result.itemid"
                        type="button"
                        class="picker-row"
                        :class="{ active: index === pickerIndex, added: lines.some((l) => l.itemid === result.itemid) }"
                        @mouseenter="pickerIndex = index"
                        @click="addLine(result)"
                      >
                        <span class="picker-avatar">{{ initials(result.itemname) }}</span>
                        <span class="picker-text">
                          <strong>{{ result.itemname }}</strong>
                          <small>{{ result.itemcode || "" }}<template v-if="result.categoryname"> &middot; {{ result.categoryname }}</template></small>
                        </span>
                        <span class="picker-stock" :class="{ zero: !result.systemqty }">{{ formatQty(result.systemqty) }} {{ result.unit || "" }}</span>
                        <span v-if="lines.some((l) => l.itemid === result.itemid)" class="picker-added"><i class="fas fa-check"></i> Added</span>
                      </button>
                    </template>
                    <div v-else class="picker-empty">No items match "{{ pickerSearch }}".</div>
                  </div>
                </div>
              </div>

              <div v-if="!lines.length" class="sa-card sa-empty">
                <div class="sa-empty-icon"><i class="fas fa-sliders-h"></i></div>
                <strong>{{ readonly ? "No items on this adjustment" : "Add the items you want to correct" }}</strong>
                <span v-if="!readonly">Search or scan above. For each item, choose <b>Remove</b> (damaged, expired, lost...) or <b>Add</b> (found extra), the quantity and a reason.</span>
              </div>

              <div v-for="(line, index) in lines" :key="line.key" class="sa-card line-card" :class="[`dir-${line.direction.toLowerCase()}`, { invalid: showErrors && !(Number(line.quantity) > 0) }]">
                <div class="line-top">
                  <span class="line-avatar">{{ initials(line.itemname) }}</span>
                  <div class="line-title">
                    <strong>{{ line.itemname }}</strong>
                    <small>{{ line.itemcode }}</small>
                  </div>
                  <div v-if="!readonly" class="line-stock" :class="{ negative: newStock(line) < 0 }">
                    <span class="ls-label">Stock</span>
                    <span class="ls-from">{{ formatQty(line.currentqty) }}</span>
                    <i class="fas fa-long-arrow-alt-right"></i>
                    <span class="ls-to">{{ formatQty(newStock(line)) }}</span>
                    <span class="ls-unit">{{ line.unit }}</span>
                  </div>
                  <div v-else class="line-stock" :class="line.direction === 'IN' ? 'pos' : 'neg'">
                    <span class="ls-to">{{ line.direction === "IN" ? "+" : "−" }}{{ formatQty(line.quantity) }}</span>
                    <span class="ls-unit">{{ line.unit }}</span>
                  </div>
                  <button v-if="!readonly" type="button" class="line-remove" title="Remove this item" aria-label="Remove this item" @click="lines.splice(index, 1)"><i class="fas fa-times"></i></button>
                </div>

                <div class="line-controls">
                  <div class="ctrl">
                    <span class="ctrl-label">Change</span>
                    <div class="dir-switch" role="radiogroup" aria-label="Add or remove stock">
                      <button type="button" class="out" role="radio" :aria-checked="line.direction === 'OUT'" :class="{ on: line.direction === 'OUT' }" :disabled="readonly" @click="setDirection(line, 'OUT')">
                        <i class="fas fa-minus-circle"></i> Remove
                      </button>
                      <button type="button" class="in" role="radio" :aria-checked="line.direction === 'IN'" :class="{ on: line.direction === 'IN' }" :disabled="readonly" @click="setDirection(line, 'IN')">
                        <i class="fas fa-plus-circle"></i> Add
                      </button>
                    </div>
                  </div>

                  <div class="ctrl">
                    <span class="ctrl-label">Quantity</span>
                    <div class="stepper" :class="{ invalid: showErrors && !(Number(line.quantity) > 0) }">
                      <button type="button" :disabled="readonly" aria-label="Decrease" @click="stepQty(line, -1)">−</button>
                      <input
                        v-model.number="line.quantity"
                        type="number"
                        min="0"
                        step="any"
                        class="qty-input"
                        placeholder="0"
                        :disabled="readonly"
                        @focus="$event.target.select()"
                      />
                      <button type="button" :disabled="readonly" aria-label="Increase" @click="stepQty(line, 1)">+</button>
                    </div>
                  </div>

                  <div class="ctrl ctrl-grow">
                    <span class="ctrl-label">Reason</span>
                    <div class="reason-chips">
                      <button
                        v-for="reason in reasonsFor(line.direction)"
                        :key="reason.value"
                        type="button"
                        class="chip"
                        :class="{ on: line.reason === reason.value }"
                        :disabled="readonly && line.reason !== reason.value"
                        @click="!readonly && (line.reason = reason.value)"
                      >
                        <i :class="reason.icon"></i> {{ reason.label }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="line-extra">
                  <template v-if="readonly">
                    <span v-if="line.notes" class="extra-note"><i class="far fa-comment"></i> {{ line.notes }}</span>
                    <span v-for="(allocation, aIndex) in line.allocations" :key="aIndex" class="extra-tag">
                      {{ allocation.batchno || (allocation.purchaseitemid ? "Batch" : "Unbatched") }}: {{ formatQty(allocation.quantity) }} @ Rs. {{ formatNumber(allocation.unitcost) }}
                    </span>
                  </template>
                  <template v-else>
                    <input v-model.trim="line.notes" type="text" class="note-input" placeholder="Add a note (optional)" />
                    <button type="button" class="more-btn" :aria-expanded="!!line.showMore" @click="toggleMore(line)">
                      <template v-if="line.direction === 'OUT'">
                        <i class="fas fa-layer-group"></i> {{ batchSummary(line) }}
                      </template>
                      <template v-else>
                        <i class="fas fa-tag"></i> Cost Rs. {{ formatNumber(line.unitcost) }}<template v-if="line.batchno"> &middot; {{ line.batchno }}</template>
                      </template>
                      <i class="fas fa-chevron-down more-chevron" :class="{ open: line.showMore }"></i>
                    </button>
                  </template>
                </div>

                <div v-if="line.showMore && !readonly" class="line-more">
                  <template v-if="line.direction === 'OUT'">
                    <span class="ctrl-label">Take stock from</span>
                    <div class="batch-options">
                      <label class="batch-option" :class="{ on: !line.purchaseitemid }">
                        <input v-model="line.purchaseitemid" type="radio" :value="null" />
                        <span>
                          <strong>{{ line.reason === "EXPIRED" ? "Earliest expiry first" : "Oldest batch first" }}</strong>
                          <small>Recommended</small>
                        </span>
                      </label>
                      <label v-for="batch in line.batches" :key="batch.purchaseitemid" class="batch-option" :class="{ on: line.purchaseitemid === batch.purchaseitemid }">
                        <input v-model="line.purchaseitemid" type="radio" :value="batch.purchaseitemid" />
                        <span>
                          <strong>{{ batch.batchno || "No batch no." }}</strong>
                          <small>{{ formatQty(batch.remainingqty) }} left<template v-if="batch.expirydate"> &middot; exp {{ formatDate(batch.expirydate) }}</template><template v-if="batch.billno"> &middot; {{ batch.billno }}</template></small>
                        </span>
                      </label>
                      <span v-if="line.batchesLoaded && !line.batches.length" class="muted">No separate batches for this item.</span>
                    </div>
                  </template>
                  <div v-else class="in-grid">
                    <label>
                      <span class="ctrl-label">Cost per {{ line.unit || "unit" }}</span>
                      <div class="money-input"><span>Rs.</span><input v-model.number="line.unitcost" type="number" min="0" step="any" /></div>
                    </label>
                    <label>
                      <span class="ctrl-label">Batch no. <em>optional</em></span>
                      <input v-model.trim="line.batchno" type="text" placeholder="e.g. B-2026" />
                    </label>
                    <label>
                      <span class="ctrl-label">Expiry <em>optional</em></span>
                      <input v-model="line.expirydate" type="date" />
                    </label>
                  </div>
                </div>
              </div>
            </template>

            <!-- ═════════════ STOCK COUNT ═════════════ -->
            <template v-else>
              <div v-if="!readonly" class="sa-card count-setup">
                <div class="count-setup-text">
                  <strong>{{ sheet.length ? "Counting" : "What are you counting?" }}</strong>
                  <small>{{ sheet.length ? `${sheet.length} items loaded` : "Pick a category, or count everything." }}</small>
                </div>
                <select v-model="countCategory" class="setup-select" aria-label="Category">
                  <option value="">All categories</option>
                  <option v-for="category in categories" :key="category.categoryid" :value="category.categoryid">{{ category.categoryname }}</option>
                </select>
                <label class="setup-check"><input v-model="countInStockOnly" type="checkbox" /> Only items in stock</label>
                <button type="button" class="setup-btn" :disabled="sheetLoading" @click="loadSheet()">
                  <i class="fas" :class="sheetLoading ? 'fa-spinner fa-spin' : sheet.length ? 'fa-sync-alt' : 'fa-download'"></i>
                  {{ sheetLoading ? "Loading..." : sheet.length ? "Load more items" : "Load items" }}
                </button>
              </div>

              <div v-if="countRows.length" class="sa-card count-card">
                <div class="progress-row">
                  <div class="progress-text">
                    <strong>{{ countProgress.counted }} of {{ countProgress.total }} counted</strong>
                    <span v-if="countProgress.diff" class="diff-badge">{{ countProgress.diff }} with a difference</span>
                    <span v-else-if="countProgress.counted" class="ok-badge"><i class="fas fa-check"></i> All counted items match</span>
                  </div>
                  <div class="progress-bar"><span :style="{ width: `${countProgress.pct}%` }"></span></div>
                </div>

                <div class="count-filters">
                  <div class="count-search">
                    <i class="fas fa-search" aria-hidden="true"></i>
                    <input v-model.trim="countSearch" type="search" placeholder="Find item or scan barcode..." @keydown.enter.prevent="jumpToSearch" />
                  </div>
                  <div class="seg">
                    <button v-for="tab in countTabs" :key="tab.value" type="button" :class="{ active: countFilter === tab.value }" @click="countFilter = tab.value">
                      {{ tab.label }} <span>{{ tab.count }}</span>
                    </button>
                  </div>
                  <button v-if="!readonly && countProgress.pending" type="button" class="link-btn" @click="fillRemaining">
                    <i class="fas fa-check-double"></i> Mark {{ countProgress.pending }} not counted as matching
                  </button>
                </div>

                <div v-if="sheetCategories.length > 1" class="cat-chips">
                  <button type="button" class="chip" :class="{ on: !categoryChip }" @click="categoryChip = ''">All</button>
                  <button v-for="category in sheetCategories" :key="category" type="button" class="chip" :class="{ on: categoryChip === category }" @click="categoryChip = category">{{ category }}</button>
                </div>

                <div class="count-list">
                  <div class="count-head">
                    <span>Item</span>
                    <span class="num">System</span>
                    <span class="center">Counted</span>
                    <span class="num">Difference</span>
                  </div>
                  <div
                    v-for="row in visibleCountRows"
                    :key="row.itemid"
                    class="count-row"
                    :class="{ done: row.counted !== '', short: row.counted !== '' && row.difference < 0, extra: row.counted !== '' && row.difference > 0 }"
                  >
                    <div class="cr-item">
                      <strong>{{ row.itemname }}</strong>
                      <small>{{ row.itemcode }}<template v-if="row.categoryname"> &middot; {{ row.categoryname }}</template></small>
                    </div>
                    <div class="cr-system num">
                      <span class="cr-label">System</span>
                      {{ formatQty(row.systemqty) }} <small>{{ row.unit }}</small>
                    </div>
                    <div class="cr-count">
                      <template v-if="!readonly">
                        <div class="stepper small">
                          <button type="button" aria-label="Decrease" @click="stepCount(row, -1)">−</button>
                          <input
                            v-model="counts[row.itemid]"
                            type="number"
                            min="0"
                            step="any"
                            class="count-input"
                            :placeholder="'—'"
                            @focus="$event.target.select()"
                            @keydown.enter.prevent="focusNextCount($event)"
                          />
                          <button type="button" aria-label="Increase" @click="stepCount(row, 1)">+</button>
                        </div>
                        <button v-if="row.counted === ''" type="button" class="match-btn" title="Counted quantity matches the system" @click="markMatch(row)"><i class="fas fa-check"></i> Matches</button>
                        <button v-else type="button" class="clear-btn" title="Clear count" aria-label="Clear count" @click="clearCount(row)"><i class="fas fa-undo"></i></button>
                      </template>
                      <strong v-else>{{ formatQty(row.counted) }}</strong>
                    </div>
                    <div class="cr-diff num">
                      <span v-if="row.counted === ''" class="muted">not counted</span>
                      <span v-else-if="row.difference === 0" class="match"><i class="fas fa-check-circle"></i> Matches</span>
                      <span v-else :class="row.difference > 0 ? 'pos' : 'neg'">
                        <strong>{{ row.difference > 0 ? "+" : "−" }}{{ formatQty(Math.abs(row.difference)) }}</strong>
                        <small>{{ money(row.difference * row.unitcost, true) }}</small>
                      </span>
                    </div>
                  </div>
                  <div v-if="!visibleCountRows.length" class="count-none">Nothing to show for this filter.</div>
                </div>
                <p v-if="!readonly" class="hint"><i class="fas fa-lightbulb"></i> Type a number and press <kbd>Enter</kbd> to move to the next item. The difference is taken against the system stock when you save, so sales made after counting are not lost.</p>
              </div>

              <div v-else-if="!sheetLoading" class="sa-card sa-empty">
                <div class="sa-empty-icon"><i class="fas fa-clipboard-check"></i></div>
                <strong>{{ readonly ? "No items on this count" : "Load the items you are going to count" }}</strong>
                <span v-if="!readonly">Choose a category above (or all items) and press <b>Load items</b>. Then type what you find on the shelf.</span>
              </div>
            </template>
          </div>

          <!-- ═════════════ SIDE PANEL ═════════════ -->
          <aside class="sa-side">
            <div class="sa-card side-card">
              <h5>Summary</h5>
              <div class="side-figures">
                <div class="fig">
                  <span>{{ isCount ? "Counted" : "Items" }}</span>
                  <strong>{{ summary.lines }}</strong>
                </div>
                <div class="fig pos">
                  <span>Added</span>
                  <strong>+{{ formatQty(summary.inQty) }}</strong>
                  <small>{{ money(summary.inValue) }}</small>
                </div>
                <div class="fig neg">
                  <span>Removed</span>
                  <strong>−{{ formatQty(summary.outQty) }}</strong>
                  <small>{{ money(summary.outValue) }}</small>
                </div>
              </div>
              <div class="side-net" :class="{ negative: summary.value < 0, zero: !summary.value }">
                <span>Stock value change</span>
                <strong>{{ money(summary.value, true) }}</strong>
              </div>

              <div class="side-fields">
                <label>
                  <span class="ctrl-label">Date</span>
                  <input v-model="header.adjustmentdate" type="date" :disabled="readonly" />
                </label>
                <label>
                  <span class="ctrl-label">Remarks <em>optional</em></span>
                  <textarea v-model="header.remarks" rows="2" :placeholder="isCount ? 'e.g. Monthly count, Rack A' : 'e.g. Rats damaged packets in store room'" :disabled="readonly"></textarea>
                </label>
              </div>

              <div class="side-actions">
                <template v-if="!readonly">
                  <button type="button" class="btn btn-submit w-100" :disabled="saving" @click="saveAndPost()">
                    <i class="fas fa-check me-1"></i>{{ saving ? "Please wait..." : isCount ? "Post count" : "Post adjustment" }}
                  </button>
                  <button type="button" class="btn side-btn w-100" :disabled="saving" @click="saveDraft()">
                    {{ adjustmentId ? "Save changes" : "Save as draft" }}
                  </button>
                </template>
                <button v-if="adjustmentId && header.status !== 'CANCELLED'" type="button" class="btn side-btn danger w-100" :disabled="saving" @click="cancelDocument()">
                  {{ header.status === "POSTED" ? "Cancel & reverse" : "Discard draft" }}
                </button>
                <router-link :to="listLink" class="side-link">Back to list</router-link>
              </div>
              <p v-if="!readonly" class="side-hint">Nothing changes in stock until you post. A posted {{ isCount ? "count" : "adjustment" }} can be cancelled later to reverse it.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

const REASONS = [
  { value: "DAMAGED", label: "Damaged", icon: "fas fa-heart-broken", dirs: ["OUT"] },
  { value: "EXPIRED", label: "Expired", icon: "fas fa-hourglass-end", dirs: ["OUT"] },
  { value: "THEFT", label: "Lost / theft", icon: "fas fa-user-secret", dirs: ["OUT"] },
  { value: "INTERNAL_USE", label: "Used in shop", icon: "fas fa-store", dirs: ["OUT"] },
  { value: "SAMPLE", label: "Free sample", icon: "fas fa-gift", dirs: ["OUT"] },
  { value: "FOUND", label: "Found extra", icon: "fas fa-search-plus", dirs: ["IN"] },
  { value: "OPENING_CORRECTION", label: "Opening stock fix", icon: "fas fa-wrench", dirs: ["IN", "OUT"] },
  { value: "COUNT_DIFFERENCE", label: "Count difference", icon: "fas fa-calculator", dirs: ["IN", "OUT"] },
  { value: "OTHER", label: "Other", icon: "fas fa-ellipsis-h", dirs: ["IN", "OUT"] },
];

let lineKey = 0;

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  name: "stockadjustment",
  data() {
    return {
      loading: false,
      saving: false,
      showErrors: false,
      header: { adjustmenttype: "ADJUSTMENT", adjustmentno: "", adjustmentdate: today(), status: "NEW", remarks: "", postedat: null, cancelledat: null },
      // adjustment mode
      lines: [],
      pickerSearch: "",
      pickerResults: [],
      pickerLoading: false,
      pickerSearched: false,
      pickerIndex: 0,
      pickerTimer: null,
      // count mode
      categories: [],
      countCategory: "",
      countInStockOnly: false,
      sheet: [],
      sheetLoading: false,
      counts: {},
      savedCountLines: [],
      countSearch: "",
      countFilter: "all",
      categoryChip: "",
    };
  },
  computed: {
    adjustmentId() {
      return this.$route.query.id || "";
    },
    isCount() {
      return this.header.adjustmenttype === "STOCK_COUNT";
    },
    readonly() {
      return Boolean(this.adjustmentId) && this.header.status !== "DRAFT";
    },
    title() {
      if (this.isCount) return this.adjustmentId ? "Stock Count" : "New Stock Count";
      return this.adjustmentId ? "Stock Adjustment" : "New Stock Adjustment";
    },
    subtitle() {
      return this.isCount ? "Count what is on the shelf and correct the difference" : "Add or remove stock with a reason";
    },
    statusLabel() {
      return { DRAFT: "Draft", POSTED: "Posted", CANCELLED: "Cancelled" }[this.header.status] || "";
    },
    listLink() {
      return { path: "/stockadjustmentlist", query: this.isCount ? { view: "counts" } : {} };
    },
    countRows() {
      if (this.readonly) {
        return this.savedCountLines.map((line) => ({
          itemid: line.itemid,
          itemname: line.itemname,
          itemcode: line.itemcode,
          unit: line.unit,
          categoryname: "",
          systemqty: Number(line.systemqty || 0),
          counted: Number(line.countedqty || 0),
          difference: Number(((line.countedqty || 0) - (line.systemqty || 0)).toFixed(2)),
          unitcost: Number(line.unitcost || 0),
        }));
      }
      return this.sheet.map((row) => {
        const raw = this.counts[row.itemid];
        const counted = raw === undefined || raw === null || raw === "" ? "" : Number(raw);
        const difference = counted === "" ? 0 : Number((counted - row.systemqty).toFixed(2));
        return { ...row, counted, difference };
      });
    },
    countTabs() {
      const rows = this.countRows;
      return [
        { value: "all", label: "All", count: rows.length },
        { value: "counted", label: "Counted", count: rows.filter((r) => r.counted !== "").length },
        { value: "diff", label: "Differences", count: rows.filter((r) => r.counted !== "" && r.difference !== 0).length },
        { value: "pending", label: "Not counted", count: rows.filter((r) => r.counted === "").length },
      ];
    },
    countProgress() {
      const total = this.countRows.length;
      const counted = this.countRows.filter((r) => r.counted !== "").length;
      const diff = this.countRows.filter((r) => r.counted !== "" && r.difference !== 0).length;
      return { total, counted, diff, pending: total - counted, pct: total ? Math.round((counted / total) * 100) : 0 };
    },
    sheetCategories() {
      return [...new Set(this.countRows.map((r) => r.categoryname).filter(Boolean))].sort();
    },
    visibleCountRows() {
      const term = this.countSearch.toLowerCase();
      return this.countRows.filter((row) => {
        if (this.categoryChip && row.categoryname !== this.categoryChip) return false;
        if (this.countFilter === "counted" && row.counted === "") return false;
        if (this.countFilter === "diff" && (row.counted === "" || row.difference === 0)) return false;
        if (this.countFilter === "pending" && row.counted !== "") return false;
        if (!term) return true;
        return [row.itemname, row.itemcode, row.categoryname].some((text) => String(text || "").toLowerCase().includes(term));
      });
    },
    summary() {
      if (this.isCount) {
        const counted = this.countRows.filter((r) => r.counted !== "");
        const ins = counted.filter((r) => r.difference > 0);
        const outs = counted.filter((r) => r.difference < 0);
        const inQty = ins.reduce((s, r) => s + r.difference, 0);
        const outQty = outs.reduce((s, r) => s - r.difference, 0);
        const inValue = ins.reduce((s, r) => s + r.difference * r.unitcost, 0);
        const outValue = outs.reduce((s, r) => s - r.difference * r.unitcost, 0);
        return { lines: counted.length, changed: ins.length + outs.length, inQty, outQty, inValue, outValue, value: inValue - outValue };
      }
      const qty = (line) => Number(line.quantity) || 0;
      const ins = this.lines.filter((l) => l.direction === "IN");
      const outs = this.lines.filter((l) => l.direction === "OUT");
      const inValue = ins.reduce((s, l) => s + qty(l) * (Number(l.unitcost) || 0), 0);
      const outValue = outs.reduce((s, l) => s + qty(l) * (Number(l.unitcost) || 0), 0);
      return {
        lines: this.lines.length,
        changed: this.lines.length,
        inQty: ins.reduce((s, l) => s + qty(l), 0),
        outQty: outs.reduce((s, l) => s + qty(l), 0),
        inValue,
        outValue,
        value: inValue - outValue,
      };
    },
  },
  watch: {
    "$route.query.id"(newId, oldId) {
      if (newId !== oldId) this.init();
    },
  },
  mounted() {
    this.init();
  },
  beforeUnmount() {
    clearTimeout(this.pickerTimer);
  },
  methods: {
    async init() {
      this.lines = [];
      this.counts = {};
      this.sheet = [];
      this.savedCountLines = [];
      this.showErrors = false;
      if (this.adjustmentId) {
        await this.loadDocument();
      } else {
        this.header = {
          adjustmenttype: this.$route.query.type === "STOCK_COUNT" ? "STOCK_COUNT" : "ADJUSTMENT",
          adjustmentno: "",
          adjustmentdate: today(),
          status: "NEW",
          remarks: "",
          postedat: null,
          cancelledat: null,
        };
      }
      if (this.isCount && !this.readonly) this.loadCategories();
      this.$nextTick(() => this.$refs.pickerInput?.focus());
    },
    async loadDocument() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/v1/stock-adjustments/${this.adjustmentId}`);
        this.header = {
          adjustmenttype: data.adjustmenttype,
          adjustmentno: data.adjustmentno,
          adjustmentdate: data.adjustmentdate,
          status: data.status,
          remarks: data.remarks || "",
          postedat: data.postedat,
          cancelledat: data.cancelledat,
        };
        if (data.adjustmenttype === "STOCK_COUNT") {
          this.savedCountLines = data.items || [];
          this.counts = Object.fromEntries((data.items || []).map((line) => [line.itemid, line.countedqty]));
          if (data.status === "DRAFT") await this.loadSheet({ onlyItems: (data.items || []).map((line) => line.itemid) });
        } else {
          const stock = data.status === "DRAFT" ? await this.fetchStockFor((data.items || []).map((l) => l.itemid)) : {};
          this.lines = (data.items || []).map((line) => ({
            key: ++lineKey,
            itemid: line.itemid,
            itemname: line.itemname,
            itemcode: line.itemcode,
            unit: line.unit,
            currentqty: stock[line.itemid] ?? 0,
            direction: line.direction,
            quantity: line.quantity,
            purchaseitemid: line.purchaseitemid || null,
            batchLabel: line.purchaseitemid ? line.batchno : "",
            unitcost: line.unitcost,
            reason: line.reason,
            notes: line.notes || "",
            batchno: line.direction === "IN" ? line.batchno || "" : "",
            expirydate: line.direction === "IN" ? line.expirydate || "" : "",
            batches: [],
            batchesLoaded: false,
            allocations: line.allocations || [],
            showMore: false,
          }));
          this.lines.filter((l) => l.direction === "OUT" && l.purchaseitemid).forEach((l) => this.loadBatches(l));
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not load", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    async fetchStockFor(itemIds) {
      const wanted = new Set(itemIds);
      try {
        const { data } = await axios.get("/v1/stock-adjustments/stock-sheet", { params: { limit: 2000 } });
        return Object.fromEntries((data || []).filter((row) => wanted.has(row.itemid)).map((row) => [row.itemid, row.systemqty]));
      } catch (error) {
        return {};
      }
    },

    // ── adjustment mode ──
    searchItems() {
      clearTimeout(this.pickerTimer);
      this.pickerIndex = 0;
      if (!this.pickerSearch) {
        this.pickerResults = [];
        this.pickerSearched = false;
        return;
      }
      this.pickerTimer = setTimeout(async () => {
        this.pickerLoading = true;
        try {
          const { data } = await axios.get("/v1/stock-adjustments/stock-sheet", { params: { search: this.pickerSearch, limit: 12 } });
          this.pickerResults = data || [];
        } catch (error) {
          this.pickerResults = [];
        } finally {
          this.pickerLoading = false;
          this.pickerSearched = true;
        }
      }, 250);
    },
    movePicker(step) {
      if (!this.pickerResults.length) return;
      this.pickerIndex = (this.pickerIndex + step + this.pickerResults.length) % this.pickerResults.length;
    },
    pickActive() {
      const result = this.pickerResults[this.pickerIndex];
      if (result) this.addLine(result);
    },
    addLine(result) {
      const existing = this.lines.find((line) => line.itemid === result.itemid);
      if (existing) {
        Swal.fire({ toast: true, position: "top-end", icon: "info", title: `${result.itemname} is already in the list`, showConfirmButton: false, timer: 1500 });
      } else {
        this.lines.push({
          key: ++lineKey,
          itemid: result.itemid,
          itemname: result.itemname,
          itemcode: result.itemcode,
          unit: result.unit,
          currentqty: Number(result.systemqty || 0),
          direction: Number(result.systemqty || 0) > 0 ? "OUT" : "IN",
          quantity: "",
          purchaseitemid: null,
          batchLabel: "",
          unitcost: Number(result.unitcost || 0),
          reason: Number(result.systemqty || 0) > 0 ? "DAMAGED" : "FOUND",
          notes: "",
          batchno: "",
          expirydate: "",
          batches: [],
          batchesLoaded: false,
          allocations: [],
          showMore: false,
        });
      }
      this.pickerSearch = "";
      this.pickerResults = [];
      this.pickerSearched = false;
      this.$nextTick(() => {
        const card = this.$el.querySelector(`.line-card:nth-of-type(${this.lines.findIndex((l) => l.itemid === result.itemid) + 1}) .qty-input`)
          || Array.from(this.$el.querySelectorAll(".line-card .qty-input")).pop();
        card?.focus();
      });
    },
    setDirection(line, direction) {
      if (line.direction === direction) return;
      line.direction = direction;
      if (!this.reasonsFor(direction).some((reason) => reason.value === line.reason)) {
        line.reason = direction === "IN" ? "FOUND" : "DAMAGED";
      }
      if (direction === "IN") line.purchaseitemid = null;
    },
    initials(name) {
      return String(name || "?").trim().split(/\s+/).slice(0, 2).map((word) => word[0]?.toUpperCase() || "").join("") || "?";
    },
    stepQty(line, delta) {
      const next = Math.max(0, (Number(line.quantity) || 0) + delta);
      line.quantity = Number(next.toFixed(2));
    },
    toggleMore(line) {
      line.showMore = !line.showMore;
      if (line.showMore && line.direction === "OUT") this.loadBatches(line);
    },
    batchSummary(line) {
      if (!line.purchaseitemid) return line.reason === "EXPIRED" ? "Earliest expiry first" : "Oldest batch first";
      const batch = line.batches.find((b) => b.purchaseitemid === line.purchaseitemid);
      return batch ? `Batch ${batch.batchno || "-"}` : line.batchLabel ? `Batch ${line.batchLabel}` : "Chosen batch";
    },
    stepCount(row, delta) {
      const base = row.counted === "" ? row.systemqty : row.counted;
      this.counts = { ...this.counts, [row.itemid]: Math.max(0, Number((Number(base) + delta).toFixed(2))) };
    },
    markMatch(row) {
      this.counts = { ...this.counts, [row.itemid]: row.systemqty };
    },
    clearCount(row) {
      const next = { ...this.counts };
      delete next[row.itemid];
      this.counts = next;
    },
    async fillRemaining() {
      const pending = this.countRows.filter((r) => r.counted === "");
      if (!pending.length) return;
      const result = await Swal.fire({
        icon: "question",
        title: `Mark ${pending.length} items as matching?`,
        text: "Use this when you checked the rest and their stock is correct. You can still change any of them.",
        showCancelButton: true,
        confirmButtonText: "Yes, mark as matching",
      });
      if (!result.isConfirmed) return;
      const next = { ...this.counts };
      pending.forEach((row) => { next[row.itemid] = row.systemqty; });
      this.counts = next;
    },
    jumpToSearch() {
      const first = this.visibleCountRows[0];
      if (!first) return;
      this.$nextTick(() => {
        const input = this.$el.querySelector(".count-row .count-input");
        input?.focus();
        input?.select?.();
      });
    },
    reasonsFor(direction) {
      return REASONS.filter((reason) => reason.dirs.includes(direction));
    },
    newStock(line) {
      const qty = Number(line.quantity) || 0;
      return Number((line.currentqty + (line.direction === "IN" ? qty : -qty)).toFixed(2));
    },
    async loadBatches(line) {
      if (line.batchesLoaded) return;
      line.batchesLoaded = true;
      try {
        const { data } = await axios.get(`/v1/stock-adjustments/item-batches/${line.itemid}`);
        line.batches = data || [];
      } catch (error) {
        line.batches = [];
      }
    },

    // ── count mode ──
    async loadCategories() {
      try {
        const { data } = await axios.get("/v1/category");
        this.categories = Array.isArray(data) ? data : data?.items || [];
      } catch (error) {
        this.categories = [];
      }
    },
    async loadSheet({ onlyItems = null } = {}) {
      this.sheetLoading = true;
      try {
        const { data } = await axios.get("/v1/stock-adjustments/stock-sheet", {
          params: {
            categoryid: onlyItems ? undefined : this.countCategory || undefined,
            in_stock_only: onlyItems ? false : this.countInStockOnly,
            limit: 2000,
          },
        });
        let rows = data || [];
        if (onlyItems) {
          rows = rows.filter((row) => onlyItems.includes(row.itemid));
        } else {
          // keep items already counted that fall outside the new filter
          const keep = this.sheet.filter((row) => this.counts[row.itemid] !== undefined && this.counts[row.itemid] !== "" && !rows.some((r) => r.itemid === row.itemid));
          rows = [...keep, ...rows];
        }
        this.sheet = rows;
        if (!rows.length) Swal.fire({ toast: true, position: "top-end", icon: "info", title: "No items found for this filter", showConfirmButton: false, timer: 1800 });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not load items", text: error.response?.data?.detail || error.message });
      } finally {
        this.sheetLoading = false;
      }
    },
    focusNextCount(event) {
      const inputs = Array.from(this.$el.querySelectorAll(".count-list .count-input"));
      const next = inputs[inputs.indexOf(event.target) + 1];
      if (next) {
        next.focus();
        next.select?.();
      }
    },

    // ── save / post ──
    buildPayload() {
      if (this.isCount) {
        const items = this.countRows
          .filter((row) => row.counted !== "")
          .map((row) => ({
            itemid: row.itemid,
            direction: "IN",
            quantity: 1,
            countedqty: Number(row.counted),
            unitcost: row.unitcost,
            reason: "COUNT_DIFFERENCE",
          }));
        return { adjustmenttype: "STOCK_COUNT", adjustmentdate: this.header.adjustmentdate, remarks: this.header.remarks || null, items };
      }
      return {
        adjustmenttype: "ADJUSTMENT",
        adjustmentdate: this.header.adjustmentdate,
        remarks: this.header.remarks || null,
        items: this.lines.map((line) => ({
          itemid: line.itemid,
          direction: line.direction,
          quantity: Number(line.quantity),
          purchaseitemid: line.direction === "OUT" ? line.purchaseitemid || null : null,
          unitcost: line.direction === "IN" ? Number(line.unitcost) || 0 : null,
          reason: line.reason,
          notes: line.notes || null,
          batchno: line.direction === "IN" ? line.batchno || null : null,
          expirydate: line.direction === "IN" ? line.expirydate || null : null,
          systemqty: line.currentqty,
        })),
      };
    },
    validate() {
      const fail = (text) => {
        this.showErrors = true;
        Swal.fire({ icon: "warning", title: "Almost there", text });
        return false;
      };
      if (this.isCount) {
        if (!this.countRows.some((row) => row.counted !== "")) return fail("Enter the counted quantity for at least one item.");
        const negative = this.countRows.find((row) => row.counted !== "" && row.counted < 0);
        if (negative) return fail(`Counted quantity for ${negative.itemname} can't be negative.`);
        return true;
      }
      if (!this.lines.length) return fail("Add at least one item.");
      for (const line of this.lines) {
        if (!(Number(line.quantity) > 0)) return fail(`Enter a quantity for ${line.itemname}.`);
        if (line.direction === "OUT" && this.newStock(line) < 0) {
          return fail(`Only ${this.formatQty(line.currentqty)} of ${line.itemname} is in stock.`);
        }
      }
      this.showErrors = false;
      return true;
    },
    async saveDraft({ silent = false } = {}) {
      if (!this.validate()) return false;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.adjustmentId) {
          await axios.put(`/v1/stock-adjustments/${this.adjustmentId}`, payload);
          if (!silent) Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Changes saved", showConfirmButton: false, timer: 1500 });
          if (!silent) await this.loadDocument();
        } else {
          const { data } = await axios.post("/v1/stock-adjustments", payload);
          if (!silent) Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Draft saved", showConfirmButton: false, timer: 1500 });
          await this.$router.replace({ path: "/stockadjustment", query: { id: data.adjustmentid } });
        }
        return true;
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not save", text: error.response?.data?.detail || error.message });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async saveAndPost() {
      if (!this.validate()) return;
      const s = this.summary;
      if (this.isCount && !s.changed) {
        const ok = await Swal.fire({
          icon: "info",
          title: "Everything matches",
          text: "All counted items match the system stock, so nothing will change. Save the count as a draft instead?",
          showCancelButton: true,
          confirmButtonText: "Save draft",
        });
        if (ok.isConfirmed) await this.saveDraft();
        return;
      }
      const result = await Swal.fire({
        icon: "question",
        title: this.isCount ? "Post this stock count?" : "Post this adjustment?",
        html: `Stock added <strong>+${this.formatQty(s.inQty)}</strong>, removed <strong>−${this.formatQty(s.outQty)}</strong><br>Stock value change <strong>${this.money(s.value, true)}</strong><br><small>You can cancel it later to reverse the changes.</small>`,
        showCancelButton: true,
        confirmButtonText: "Yes, post",
      });
      if (!result.isConfirmed) return;
      const saved = await this.saveDraft({ silent: true });
      if (!saved || !this.$route.query.id) return;
      this.saving = true;
      try {
        await axios.post(`/v1/stock-adjustments/${this.$route.query.id}/post`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Stock updated", showConfirmButton: false, timer: 1800 });
        await this.loadDocument();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not post", text: error.response?.data?.detail || error.message });
        await this.loadDocument();
      } finally {
        this.saving = false;
      }
    },
    async cancelDocument() {
      const posted = this.header.status === "POSTED";
      const result = await Swal.fire({
        icon: "warning",
        title: posted ? "Cancel and reverse?" : "Cancel this draft?",
        text: posted ? "All stock changes from this document will be reversed." : "The draft will be marked cancelled.",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;
      this.saving = true;
      try {
        await axios.post(`/v1/stock-adjustments/${this.adjustmentId}/cancel`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: posted ? "Reversed" : "Cancelled", showConfirmButton: false, timer: 1600 });
        await this.loadDocument();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not cancel", text: error.response?.data?.detail || error.message });
      } finally {
        this.saving = false;
      }
    },

    // ── formatting ──
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    money(value, signed = false) {
      const number = Number(value || 0);
      const text = `Rs. ${Math.abs(number).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      if (!signed) return text;
      return number < 0 ? `−${text}` : number > 0 ? `+${text}` : text;
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
/* ── header ─────────────────────────────────────── */
.sa-head { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
.sa-back {
  width: 38px; height: 38px; flex: 0 0 38px; border-radius: 10px; border: 1px solid #e5e7eb; background: #fff;
  display: flex; align-items: center; justify-content: center; color: #374151;
}
.sa-back:hover { border-color: #FF9F43; color: #c2410c; }
.sa-head-text h3 { margin: 0; font-size: 20px; font-weight: 700; color: #111827; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sa-head-text p { margin: 2px 0 0; font-size: 13px; color: #6b7280; }
.sa-head-no { font-size: 13px; font-weight: 600; color: #6b7280; background: #f3f4f6; padding: 2px 10px; border-radius: 999px; font-family: ui-monospace, Menlo, monospace; }
.sa-status { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; }
.st-draft { background: #ffedd5; color: #c2410c; }
.st-posted { background: #dcfce7; color: #15803d; }
.st-cancelled { background: #f3f4f6; color: #6b7280; }

.sa-banner { display: flex; align-items: center; gap: 10px; padding: 12px 16px; margin-bottom: 16px; border-radius: 12px; font-size: 13.5px; }
.banner-posted { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
.banner-cancelled { background: #f9fafb; border: 1px solid #e5e7eb; color: #4b5563; }

/* ── layout ─────────────────────────────────────── */
.sa-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 18px; align-items: start; }
.sa-main { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.sa-card { background: #fff; border: 1px solid #e9ecf2; border-radius: 14px; padding: 16px 18px; }
.sa-loading { display: flex; align-items: center; justify-content: center; gap: 10px; min-height: 180px; color: #6b7280; }
.sa-spinner { width: 26px; height: 26px; border: 3px solid #f3f4f6; border-top-color: #FF9F43; border-radius: 50%; animation: sa-spin 0.7s linear infinite; }
@keyframes sa-spin { to { transform: rotate(360deg); } }
.muted { color: #9ca3af; font-size: 13px; }
.num { text-align: right; font-variant-numeric: tabular-nums; }
.center { text-align: center; }
.ctrl-label { display: block; font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; margin-bottom: 6px; }
.ctrl-label em { font-style: normal; text-transform: none; letter-spacing: 0; font-weight: 400; color: #9ca3af; }

.sa-empty { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; padding: 36px 20px; color: #6b7280; font-size: 14px; }
.sa-empty strong { color: #111827; font-size: 16px; }
.sa-empty span { max-width: 460px; }
.sa-empty-icon { width: 56px; height: 56px; border-radius: 16px; background: #fff4e8; color: #FF9F43; display: flex; align-items: center; justify-content: center; font-size: 22px; margin-bottom: 6px; }

/* ── item picker ────────────────────────────────── */
.sa-picker-card { padding: 12px; position: relative; z-index: 6; }
.picker { position: relative; }
.picker-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: 16px; }
.picker-input {
  width: 100%; height: 50px; padding: 0 150px 0 46px; border: 1.5px solid #e5e7eb; border-radius: 12px;
  font-size: 15px; background: #fafafa;
}
.picker-input:focus { outline: none; border-color: #FF9F43; background: #fff; box-shadow: 0 0 0 4px rgba(255, 159, 67, 0.12); }
.picker-kbd { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); font-size: 12px; color: #9ca3af; pointer-events: none; }
.picker-results {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0; background: #fff; border: 1px solid #e5e7eb;
  border-radius: 12px; box-shadow: 0 16px 32px rgba(15, 23, 42, 0.14); max-height: 360px; overflow-y: auto;
}
.picker-row { width: 100%; display: flex; align-items: center; gap: 12px; padding: 10px 14px; border: 0; background: #fff; text-align: left; }
.picker-row + .picker-row { border-top: 1px solid #f3f4f6; }
.picker-row.active { background: #fff8f0; }
.picker-row.added { opacity: 0.75; }
.picker-avatar, .line-avatar {
  flex: 0 0 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, #fff4e8, #ffe2c4);
  color: #c2410c; font-weight: 700; font-size: 13px; display: flex; align-items: center; justify-content: center;
}
.picker-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.picker-text strong { font-size: 14px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picker-text small { font-size: 12px; color: #6b7280; }
.picker-stock { font-size: 13px; font-weight: 600; color: #374151; white-space: nowrap; }
.picker-stock.zero { color: #dc2626; }
.picker-added { font-size: 12px; color: #15803d; font-weight: 600; white-space: nowrap; }
.picker-empty { padding: 14px; color: #6b7280; font-size: 13.5px; }

/* ── adjustment line card ───────────────────────── */
.line-card { border-left: 4px solid #e5e7eb; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.line-card.dir-out { border-left-color: #f87171; }
.line-card.dir-in { border-left-color: #4ade80; }
.line-card.invalid { box-shadow: 0 0 0 2px #fecaca; }
.line-top { display: flex; align-items: center; gap: 12px; }
.line-title { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.line-title strong { font-size: 15px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.line-title small { font-size: 12px; color: #9ca3af; }
.line-stock { display: flex; align-items: baseline; gap: 6px; padding: 6px 12px; border-radius: 10px; background: #f9fafb; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ls-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #9ca3af; }
.ls-from { color: #6b7280; font-size: 14px; }
.line-stock i { color: #9ca3af; font-size: 12px; }
.ls-to { font-size: 17px; font-weight: 700; color: #111827; }
.ls-unit { font-size: 12px; color: #9ca3af; }
.line-stock.negative { background: #fef2f2; }
.line-stock.negative .ls-to { color: #dc2626; }
.line-stock.pos .ls-to { color: #15803d; }
.line-stock.neg .ls-to { color: #dc2626; }
.line-remove { width: 32px; height: 32px; border: 0; border-radius: 8px; background: transparent; color: #9ca3af; }
.line-remove:hover { background: #fef2f2; color: #dc2626; }

.line-controls { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 14px; align-items: flex-start; }
.ctrl-grow { flex: 1 1 260px; min-width: 0; }
.dir-switch { display: inline-flex; padding: 3px; border-radius: 10px; background: #f3f4f6; gap: 3px; }
.dir-switch button { border: 0; background: transparent; border-radius: 8px; padding: 8px 14px; font-size: 13.5px; font-weight: 600; color: #6b7280; display: inline-flex; align-items: center; gap: 6px; }
.dir-switch button.out.on { background: #fff; color: #dc2626; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12); }
.dir-switch button.in.on { background: #fff; color: #15803d; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12); }
.dir-switch button:disabled { cursor: default; }

.stepper { display: inline-flex; align-items: stretch; border: 1.5px solid #e5e7eb; border-radius: 10px; overflow: hidden; background: #fff; }
.stepper:focus-within { border-color: #FF9F43; box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.12); }
.stepper.invalid { border-color: #f87171; }
.stepper button { width: 38px; border: 0; background: #f9fafb; color: #374151; font-size: 18px; font-weight: 600; }
.stepper button:hover:not(:disabled) { background: #fff4e8; color: #c2410c; }
.stepper input { width: 76px; height: 40px; border: 0; text-align: center; font-size: 16px; font-weight: 700; color: #111827; -moz-appearance: textfield; }
.stepper input::-webkit-outer-spin-button, .stepper input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.stepper input:focus { outline: none; }
.stepper.small button { width: 32px; font-size: 16px; }
.stepper.small input { width: 70px; height: 36px; font-size: 15px; }

.reason-chips, .cat-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip { border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563; display: inline-flex; align-items: center; gap: 6px; }
.chip i { font-size: 11px; color: #9ca3af; }
.chip:hover:not(:disabled) { border-color: #fdba74; }
.chip.on { background: #fff4e8; border-color: #FF9F43; color: #c2410c; }
.chip.on i { color: #c2410c; }
.chip:disabled { opacity: 0.45; cursor: default; }

.line-extra { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 12px; padding-top: 12px; border-top: 1px dashed #eef0f4; }
.note-input { flex: 1 1 220px; height: 34px; border: 1px solid transparent; border-radius: 8px; padding: 0 10px; font-size: 13px; background: #f9fafb; }
.note-input:focus { outline: none; border-color: #FF9F43; background: #fff; }
.more-btn { border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; height: 34px; padding: 0 12px; font-size: 12.5px; color: #374151; display: inline-flex; align-items: center; gap: 8px; }
.more-btn:hover { border-color: #fdba74; }
.more-chevron { font-size: 10px; color: #9ca3af; transition: transform 0.15s ease; }
.more-chevron.open { transform: rotate(180deg); }
.extra-note { font-size: 13px; color: #4b5563; }
.extra-tag { font-size: 12px; color: #4b5563; background: #f3f4f6; border-radius: 999px; padding: 3px 10px; }

.line-more { margin-top: 12px; padding: 12px; border-radius: 10px; background: #fafbfc; }
.batch-options { display: flex; flex-wrap: wrap; gap: 8px; }
.batch-option { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border: 1.5px solid #e5e7eb; border-radius: 10px; background: #fff; cursor: pointer; margin: 0; }
.batch-option input { accent-color: #FF9F43; }
.batch-option span { display: flex; flex-direction: column; }
.batch-option strong { font-size: 13px; color: #111827; }
.batch-option small { font-size: 11.5px; color: #6b7280; }
.batch-option.on { border-color: #FF9F43; background: #fffaf5; }
.in-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.in-grid label { margin: 0; }
.in-grid input { width: 100%; height: 36px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0 10px; font-size: 13.5px; background: #fff; }
.money-input { position: relative; }
.money-input span { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 12px; color: #9ca3af; }
.money-input input { padding-left: 32px; }

/* ── stock count ────────────────────────────────── */
.count-setup { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.count-setup-text { flex: 1 1 200px; display: flex; flex-direction: column; }
.count-setup-text strong { font-size: 15px; color: #111827; }
.count-setup-text small { font-size: 12.5px; color: #6b7280; }
.setup-select { height: 40px; min-width: 200px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 0 10px; background: #fff; font-size: 14px; }
.setup-check { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 13px; color: #374151; }
.setup-check input { accent-color: #FF9F43; }
.setup-btn { height: 40px; border: 0; border-radius: 10px; padding: 0 16px; background: #111827; color: #fff; font-size: 13.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 8px; }
.setup-btn:disabled { opacity: 0.6; }

.count-card { padding: 16px 18px 12px; }
.progress-row { margin-bottom: 14px; }
.progress-text { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.progress-text strong { font-size: 15px; color: #111827; }
.diff-badge { font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 999px; background: #fef3c7; color: #b45309; }
.ok-badge { font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 999px; background: #dcfce7; color: #15803d; }
.progress-bar { height: 8px; border-radius: 999px; background: #f1f2f4; overflow: hidden; }
.progress-bar span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #FF9F43, #fb923c); transition: width 0.25s ease; }

.count-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.count-search { position: relative; flex: 1 1 240px; max-width: 340px; }
.count-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.count-search input { width: 100%; height: 38px; padding-left: 34px; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 13.5px; }
.count-search input:focus { outline: none; border-color: #FF9F43; }
.seg { display: inline-flex; padding: 3px; border-radius: 10px; background: #f3f4f6; flex-wrap: wrap; }
.seg button { border: 0; background: transparent; padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 600; color: #6b7280; }
.seg button span { margin-left: 4px; padding: 0 7px; border-radius: 999px; background: #e5e7eb; font-size: 11px; }
.seg button.active { background: #fff; color: #c2410c; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }
.seg button.active span { background: #fff4e8; }
.link-btn { border: 0; background: transparent; color: #c2410c; font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
.link-btn:hover { text-decoration: underline; }
.cat-chips { margin-bottom: 10px; }

.count-list { border: 1px solid #eef0f4; border-radius: 12px; max-height: 620px; overflow-y: auto; }
.count-head, .count-row { display: grid; grid-template-columns: minmax(0, 1fr) 110px 240px 140px; gap: 12px; align-items: center; padding: 10px 14px; }
.count-head { position: sticky; top: 0; z-index: 1; background: #f9fafb; font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; border-bottom: 1px solid #eef0f4; }
.count-row { border-bottom: 1px solid #f3f4f6; transition: background 0.15s ease; }
.count-row:last-child { border-bottom: 0; }
.count-row:hover { background: #fcfcfd; }
.count-row.done { background: #fcfefc; }
.count-row.short { background: #fffafa; box-shadow: inset 3px 0 0 #f87171; }
.count-row.extra { background: #fafffb; box-shadow: inset 3px 0 0 #4ade80; }
.cr-item { min-width: 0; display: flex; flex-direction: column; }
.cr-item strong { font-size: 14px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cr-item small { font-size: 12px; color: #9ca3af; }
.cr-system { font-size: 14px; font-weight: 600; color: #374151; }
.cr-system small { font-weight: 400; color: #9ca3af; }
.cr-label { display: none; }
.cr-count { display: flex; align-items: center; justify-content: center; gap: 8px; }
.match-btn { border: 1px solid #bbf7d0; background: #f0fdf4; color: #15803d; border-radius: 8px; height: 36px; padding: 0 10px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.match-btn:hover { background: #dcfce7; }
.clear-btn { width: 32px; height: 36px; border: 1px solid #e5e7eb; background: #fff; color: #9ca3af; border-radius: 8px; }
.clear-btn:hover { color: #c2410c; border-color: #fdba74; }
.cr-diff { font-size: 13px; }
.cr-diff span { display: inline-flex; flex-direction: column; align-items: flex-end; }
.cr-diff strong { font-size: 15px; }
.cr-diff small { font-size: 11.5px; opacity: 0.85; }
.pos { color: #15803d; }
.neg { color: #dc2626; }
.match { color: #15803d; font-weight: 600; flex-direction: row !important; gap: 4px; }
.count-none { padding: 24px; text-align: center; color: #6b7280; font-size: 13.5px; }
.hint { margin: 10px 2px 2px; font-size: 12px; color: #9ca3af; }
.hint kbd { padding: 1px 5px; border-radius: 4px; background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; font-size: 11px; }

/* ── side panel ─────────────────────────────────── */
.sa-side { position: sticky; top: 80px; }
.side-card h5 { margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #111827; }
.side-figures { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.fig { display: flex; flex-direction: column; gap: 2px; padding: 10px; border-radius: 10px; background: #f9fafb; }
.fig span { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #6b7280; }
.fig strong { font-size: 18px; color: #111827; font-variant-numeric: tabular-nums; }
.fig small { font-size: 11px; color: #9ca3af; }
.fig.pos strong { color: #15803d; }
.fig.neg strong { color: #dc2626; }
.side-net { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; padding: 12px 14px; border-radius: 12px; background: #f0fdf4; color: #15803d; }
.side-net.negative { background: #fef2f2; color: #dc2626; }
.side-net.zero { background: #f9fafb; color: #6b7280; }
.side-net span { font-size: 13px; font-weight: 600; }
.side-net strong { font-size: 18px; font-variant-numeric: tabular-nums; }
.side-fields { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
.side-fields label { margin: 0; }
.side-fields input, .side-fields textarea { width: 100%; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; font-size: 13.5px; background: #fff; }
.side-fields input:focus, .side-fields textarea:focus { outline: none; border-color: #FF9F43; }
.side-fields input:disabled, .side-fields textarea:disabled { background: #f9fafb; }
.side-actions { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }
.side-actions .btn { min-height: 42px; font-weight: 600; }
.side-btn { border: 1px solid #e5e7eb; background: #fff; color: #374151; }
.side-btn:hover { border-color: #FF9F43; color: #c2410c; }
.side-btn.danger { color: #dc2626; }
.side-btn.danger:hover { border-color: #f87171; background: #fef2f2; color: #dc2626; }
.side-link { text-align: center; font-size: 13px; color: #6b7280; padding: 4px; }
.side-link:hover { color: #111827; }
.side-hint { margin: 10px 0 0; font-size: 11.5px; color: #9ca3af; text-align: center; }

/* ── responsive ─────────────────────────────────── */
@media (max-width: 1199px) {
  .sa-layout { grid-template-columns: 1fr; }
  .sa-side { position: static; }
  .count-head, .count-row { grid-template-columns: minmax(0, 1fr) 90px 230px 120px; }
}
@media (max-width: 767px) {
  .sa-card { padding: 14px; }
  .picker-kbd { display: none; }
  .picker-input { padding-right: 14px; }
  .line-top { flex-wrap: wrap; }
  .line-stock { order: 3; width: 100%; justify-content: flex-end; }
  .in-grid { grid-template-columns: 1fr; }
  .count-head { display: none; }
  .count-row { grid-template-columns: 1fr auto; grid-template-areas: "item item" "system diff" "count count"; row-gap: 8px; }
  .cr-item { grid-area: item; }
  .cr-system { grid-area: system; text-align: left; }
  .cr-label { display: inline; font-size: 11px; font-weight: 600; text-transform: uppercase; color: #9ca3af; margin-right: 4px; }
  .cr-diff { grid-area: diff; }
  .cr-count { grid-area: count; justify-content: flex-start; }
  .sa-side { position: sticky; bottom: 0; z-index: 8; }
  .side-card { box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.08); padding: 10px; }
  .side-card h5, .side-figures, .side-fields, .side-hint, .side-link { display: none; }
  .side-net { margin-top: 0; padding: 8px 12px; }
  .side-actions { flex-direction: row; margin-top: 8px; }
  .side-actions .btn { flex: 1; min-width: 0; font-size: 13px; }
}
</style>
